import styled from '@emotion/styled';
import { uiTheme as t, DataTable, Pagination, SearchBox } from 'Components/ui';
import type { PaginationLabels } from 'Components/ui';
import { BulkBar, BulkCount } from 'Components/ui/layout';
import useTranslation from 'Hooks/useTranslation';
import type { EligibleEmployeeRow } from '../../Hooks/useEligibleEmployeesForApproval';

interface Props {
  rows: EligibleEmployeeRow[];
  totalEmployees: number;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** Identificadores seleccionados a lo largo de todas las páginas. */
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  selectedCount: number;
  paginationLabels: PaginationLabels;
}

const Column = styled.div({ display: 'flex', flexDirection: 'column', gap: 12 });

const Loading = styled.p({ margin: 0, fontFamily: t.font, fontSize: 13.5, color: t.color.muted });

/**
 * Envoltura de la tabla mientras se recarga.
 *
 * Cambiar de página no puede desmontar la tabla: el drawer se encoge, el
 * navegador recorta el scroll a cero y el administrador acaba hasta arriba
 * cada vez que avanza una página. Se queda montada y solo se atenúa.
 */
const Busy = styled.div<{ busy: boolean }>(({ busy }) => ({
  opacity: busy ? 0.5 : 1,
  pointerEvents: busy ? 'none' : 'auto',
  transition: 'opacity 120ms ease',
}));

/**
 * Panel de multi-aprobación: elegibles con casillas, buscador y paginación.
 *
 * La paginación es del servidor y en base 0 internamente (DataGrid v5), así que
 * aquí se traduce a base 1 para `Pagination`, que es lo que espera el sistema.
 */
const EmployeeSelectionPanel = ({
  rows,
  totalEmployees,
  loading,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  search,
  onSearchChange,
  selected,
  onToggle,
  onToggleAll,
  selectedCount,
  paginationLabels,
}: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');

  const columns = [
    { key: 'payrollNum', label: t2('PayrollNum'), width: '0.7fr' },
    { key: 'fullName', label: t2('Employee'), width: '1.5fr', strong: true },
    { key: 'department', label: t2('department_name') ?? 'Departamento', width: '1fr' },
    { key: 'jobTitle', label: t2('job_title_name') ?? 'Puesto', width: '1fr' },
    { key: 'terminal', label: t2('terminal_name') ?? 'Terminal', width: '1fr' },
  ];

  return (
    <Column>
      <SearchBox value={search} placeholder={t2('search_employee') ?? 'Buscar empleado'} onChange={onSearchChange} />

      {/* Solo la primera carga sustituye la tabla: ahí todavía no hay nada que
          conservar y el vacío se leería como "no hay empleados elegibles". */}
      {loading && rows.length === 0 ? (
        <Loading>{t2('loading')}</Loading>
      ) : (
        <Busy busy={loading} aria-busy={loading}>
          <DataTable
            rows={rows.map(row => ({
              id: row.id,
              payrollNum: row.payrollNum,
              fullName: row.fullName,
              department: row.department,
              jobTitle: row.jobTitle,
              terminal: row.terminal,
            }))}
            columns={columns}
            labels={{ empty: t2('no_eligible_employees') }}
            selection={{ selected, onToggle, onToggleAll }}
          />
        </Busy>
      )}

      <Pagination
        page={page + 1}
        pageSize={pageSize}
        totalPages={Math.ceil(totalEmployees / pageSize)}
        totalItems={totalEmployees}
        labels={paginationLabels}
        onChange={next => onPageChange(next - 1)}
        onPageSizeChange={onPageSizeChange}
      />

      {selectedCount > 0 ? (
        <BulkBar>
          <BulkCount>{t2('selected_count').replace('{n}', String(selectedCount))}</BulkCount>
        </BulkBar>
      ) : null}
    </Column>
  );
};

export default EmployeeSelectionPanel;
